import React, { useState, useEffect } from 'react';
import { 
  Shield, Package, AlertTriangle, CheckCircle, ExternalLink, 
  RefreshCw, GitPullRequest, FileText, Download, ChevronDown, ChevronUp,
  AlertOctagon, Info, Lock, Unlock, Scale, Activity, Clock, Users,
  Star, GitFork, Eye, Copy, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Severity colors
const severityColors = {
  critical: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', badge: 'bg-red-500' },
  high: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', badge: 'bg-orange-500' },
  medium: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200', badge: 'bg-yellow-500' },
  low: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', badge: 'bg-blue-500' },
};

// License risk colors
const licenseRiskColors = {
  high: { bg: 'bg-red-50', text: 'text-red-600', icon: Lock },
  medium: { bg: 'bg-yellow-50', text: 'text-yellow-600', icon: AlertTriangle },
  low: { bg: 'bg-green-50', text: 'text-green-600', icon: Unlock },
  none: { bg: 'bg-gray-50', text: 'text-gray-600', icon: CheckCircle },
};

// Dependency Card Component
export const DependencyCard = ({ dependency, onViewDetails }) => {
  const [expanded, setExpanded] = useState(false);
  const riskStyle = licenseRiskColors[dependency.license_risk] || licenseRiskColors.medium;
  const RiskIcon = riskStyle.icon;
  
  const hasCritical = dependency.severity_counts?.critical > 0;
  const hasHigh = dependency.severity_counts?.high > 0;
  
  return (
    <div className={`border rounded-lg overflow-hidden ${hasCritical ? 'border-red-300' : hasHigh ? 'border-orange-300' : 'border-gray-200'}`}>
      <div 
        className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${hasCritical ? 'bg-red-50' : hasHigh ? 'bg-orange-50' : 'bg-white'}`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="w-5 h-5 text-gray-400" />
            <div>
              <h4 className="font-semibold text-gray-900">{dependency.name}</h4>
              <span className="text-sm text-gray-500">v{dependency.version}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {dependency.vulnerability_count > 0 && (
              <Badge variant="destructive" className="gap-1">
                <AlertTriangle className="w-3 h-3" />
                {dependency.vulnerability_count} vulnerabilities
              </Badge>
            )}
            <Badge className={`${riskStyle.bg} ${riskStyle.text} gap-1`}>
              <RiskIcon className="w-3 h-3" />
              {dependency.license || 'Unknown'}
            </Badge>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </div>
        
        {/* Severity breakdown */}
        {dependency.vulnerability_count > 0 && (
          <div className="flex gap-2 mt-2">
            {dependency.severity_counts?.critical > 0 && (
              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                {dependency.severity_counts.critical} Critical
              </span>
            )}
            {dependency.severity_counts?.high > 0 && (
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                {dependency.severity_counts.high} High
              </span>
            )}
            {dependency.severity_counts?.medium > 0 && (
              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                {dependency.severity_counts.medium} Medium
              </span>
            )}
            {dependency.severity_counts?.low > 0 && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                {dependency.severity_counts.low} Low
              </span>
            )}
          </div>
        )}
      </div>
      
      {expanded && (
        <div className="border-t bg-gray-50 p-4 space-y-4">
          {/* Health Metrics */}
          {dependency.health && (
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center p-2 bg-white rounded border">
                <Activity className="w-4 h-4 mx-auto mb-1 text-blue-500" />
                <div className="text-lg font-bold">{dependency.health.popularity_score}</div>
                <div className="text-xs text-gray-500">Health Score</div>
              </div>
              <div className="text-center p-2 bg-white rounded border">
                <Download className="w-4 h-4 mx-auto mb-1 text-green-500" />
                <div className="text-lg font-bold">{(dependency.health.weekly_downloads / 1000).toFixed(0)}K</div>
                <div className="text-xs text-gray-500">Weekly Downloads</div>
              </div>
              <div className="text-center p-2 bg-white rounded border">
                <Clock className="w-4 h-4 mx-auto mb-1 text-purple-500" />
                <div className="text-sm font-medium capitalize">{dependency.health.maintenance_status}</div>
                <div className="text-xs text-gray-500">Maintenance</div>
              </div>
              <div className="text-center p-2 bg-white rounded border">
                <Shield className={`w-4 h-4 mx-auto mb-1 ${dependency.health.has_security_policy ? 'text-green-500' : 'text-gray-400'}`} />
                <div className="text-sm font-medium">{dependency.health.has_security_policy ? 'Yes' : 'No'}</div>
                <div className="text-xs text-gray-500">Security Policy</div>
              </div>
            </div>
          )}
          
          {/* License Details */}
          {dependency.license_details && (
            <div className={`p-3 rounded-lg ${riskStyle.bg}`}>
              <h5 className="font-semibold text-sm mb-2 flex items-center gap-2">
                <Scale className="w-4 h-4" />
                License: {dependency.license_details.name}
              </h5>
              <p className="text-xs text-gray-600 mb-2">{dependency.license_details.description}</p>
              <div className="flex flex-wrap gap-2">
                {dependency.license_details.commercial_use && <Badge variant="outline" className="text-xs">Commercial Use</Badge>}
                {dependency.license_details.modification && <Badge variant="outline" className="text-xs">Modification</Badge>}
                {dependency.license_details.distribution && <Badge variant="outline" className="text-xs">Distribution</Badge>}
                {dependency.license_details.copyleft && <Badge variant="destructive" className="text-xs">Copyleft</Badge>}
                {dependency.license_details.patent_rights && <Badge variant="outline" className="text-xs">Patent Rights</Badge>}
              </div>
            </div>
          )}
          
          {/* Vulnerabilities List */}
          {dependency.vulnerabilities?.length > 0 && (
            <div>
              <h5 className="font-semibold text-sm mb-2 flex items-center gap-2">
                <AlertOctagon className="w-4 h-4 text-red-500" />
                Vulnerabilities ({dependency.vulnerabilities.length})
              </h5>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {dependency.vulnerabilities.map((vuln, idx) => {
                  const style = severityColors[vuln.severity] || severityColors.medium;
                  return (
                    <div key={idx} className={`p-2 rounded border ${style.bg} ${style.border}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs">{vuln.id}</span>
                        <Badge className={`${style.badge} text-white text-xs`}>{vuln.severity}</Badge>
                      </div>
                      <p className="text-sm mt-1">{vuln.title}</p>
                      {vuln.fixed_version && (
                        <p className="text-xs text-green-600 mt-1">
                          Fix available: Upgrade to v{vuln.fixed_version}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Fix PR Dialog Component
export const FixPRDialog = ({ open, onClose, scanId }) => {
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [prContent, setPrContent] = useState(null);
  const [copied, setCopied] = useState(false);
  const [prResult, setPrResult] = useState(null);
  
  useEffect(() => {
    if (open && scanId) {
      loadPRContent();
      setPrResult(null);
    }
  }, [open, scanId]);
  
  const loadPRContent = async () => {
    setLoading(true);
    try {
      const response = await api.post(`/api/litesabre/scans/${scanId}/generate-fix-pr`);
      setPrContent(response.data.pr_content);
    } catch (err) {
      console.error('Failed to generate PR:', err);
      toast.error('Failed to generate fix PR content');
    } finally {
      setLoading(false);
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard');
  };
  
  const handleCreatePR = async () => {
    setCreating(true);
    try {
      const response = await api.post(`/api/litesabre/scans/${scanId}/create-fix-pr`);
      if (response.data.success) {
        setPrResult(response.data);
        toast.success('Fix PR created successfully!');
      } else {
        toast.error(response.data.message || 'Failed to create PR');
      }
    } catch (err) {
      console.error('Failed to create PR:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to create PR. Make sure GITHUB_TOKEN is configured.';
      toast.error(errorMsg);
    } finally {
      setCreating(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitPullRequest className="w-5 h-5 text-purple-500" />
            Generate Fix Pull Request
          </DialogTitle>
          <DialogDescription>
            Create a PR to fix vulnerabilities in your dependencies
          </DialogDescription>
        </DialogHeader>
        
        {prResult?.success ? (
          <div className="flex-1 py-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">PR Created Successfully!</h3>
            <p className="text-gray-500 mb-4">
              Pull request #{prResult.pr_number} has been created on GitHub
            </p>
            <div className="space-y-2 text-sm text-gray-600 mb-6">
              <p><strong>Repository:</strong> {prResult.repository}</p>
              <p><strong>Branch:</strong> {prResult.branch_name}</p>
              <p><strong>Vulnerabilities Addressed:</strong> {prResult.vulnerabilities_addressed}</p>
            </div>
            <a 
              href={prResult.pr_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
            >
              <ExternalLink className="w-4 h-4" />
              View Pull Request on GitHub
            </a>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : prContent ? (
          <div className="flex-1 overflow-y-auto space-y-4">
            {/* PR Title */}
            <div>
              <label className="text-sm font-medium text-gray-500">PR Title</label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 p-2 bg-gray-100 rounded text-sm">{prContent.title}</code>
                <Button variant="ghost" size="icon" onClick={() => copyToClipboard(prContent.title)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Summary */}
            <div className="grid grid-cols-4 gap-3">
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{prContent.severity_breakdown?.critical || 0}</div>
                <div className="text-xs text-red-500">Critical</div>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">{prContent.severity_breakdown?.high || 0}</div>
                <div className="text-xs text-orange-500">High</div>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{prContent.severity_breakdown?.medium || 0}</div>
                <div className="text-xs text-yellow-500">Medium</div>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{prContent.severity_breakdown?.low || 0}</div>
                <div className="text-xs text-blue-500">Low</div>
              </div>
            </div>
            
            {/* Recommendations */}
            {prContent.recommendations?.length > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-500">Upgrade Recommendations</label>
                <div className="mt-2 space-y-2">
                  {prContent.recommendations.map((rec, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <Package className="w-4 h-4 text-gray-400" />
                        <div>
                          <span className="font-medium">{rec.package}</span>
                          <span className="text-gray-400 mx-2">→</span>
                          <code className="text-green-600">{rec.recommended}</code>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{rec.vulnerabilities_fixed} fixes</Badge>
                        {rec.breaking_changes && <Badge variant="destructive">Breaking</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* PR Body */}
            <div>
              <label className="text-sm font-medium text-gray-500">PR Description (Markdown)</label>
              <div className="relative mt-1">
                <pre className="p-4 bg-gray-900 text-gray-100 rounded-lg text-xs overflow-x-auto max-h-64 overflow-y-auto">
                  {prContent.body}
                </pre>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  className="absolute top-2 right-2"
                  onClick={() => copyToClipboard(prContent.body)}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <Info className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No vulnerabilities found to fix</p>
          </div>
        )}
        
        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
          {prContent && !prResult?.success && (
            <>
              <Button variant="outline" onClick={() => copyToClipboard(prContent.body)}>
                <Copy className="w-4 h-4 mr-2" />
                Copy PR Content
              </Button>
              <Button 
                onClick={handleCreatePR}
                disabled={creating}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="create-fix-pr-btn"
              >
                {creating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Creating PR...
                  </>
                ) : (
                  <>
                    <GitPullRequest className="w-4 h-4 mr-2" />
                    Create PR on GitHub
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Security Overview Component
export const SecurityOverview = ({ scanId }) => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  
  useEffect(() => {
    loadOverview();
  }, [scanId]);
  
  const loadOverview = async () => {
    try {
      const response = await api.get(`/api/litesabre/scans/${scanId}/security-overview`);
      setOverview(response.data);
    } catch (err) {
      console.error('Failed to load security overview:', err);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }
  
  if (!overview) return null;
  
  const gradeColors = {
    A: 'bg-green-500',
    B: 'bg-blue-500',
    C: 'bg-yellow-500',
    D: 'bg-orange-500',
    F: 'bg-red-500'
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-500" />
          Security Overview
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4 mb-6">
          {/* Security Score */}
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${gradeColors[overview.security_grade]} text-white text-2xl font-bold mb-2`}>
              {overview.security_grade}
            </div>
            <div className="text-3xl font-bold">{overview.security_score}</div>
            <div className="text-sm text-gray-500">Security Score</div>
          </div>
          
          {/* Vulnerabilities */}
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <AlertTriangle className={`w-10 h-10 mx-auto mb-2 ${overview.vulnerability_summary.total > 0 ? 'text-red-500' : 'text-green-500'}`} />
            <div className="text-3xl font-bold">{overview.vulnerability_summary.total}</div>
            <div className="text-sm text-gray-500">Total Vulnerabilities</div>
          </div>
          
          {/* Fix Rate */}
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500" />
            <div className="text-3xl font-bold">{overview.vulnerability_summary.fix_rate}%</div>
            <div className="text-sm text-gray-500">Fixable</div>
          </div>
        </div>
        
        {/* Severity Breakdown */}
        <div className="flex gap-2">
          {Object.entries(overview.vulnerability_summary.by_severity).map(([sev, count]) => {
            if (count === 0 || sev === 'unknown') return null;
            const style = severityColors[sev] || severityColors.medium;
            return (
              <Badge key={sev} className={`${style.badge} text-white`}>
                {count} {sev}
              </Badge>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// Dependencies Tab Component
export const DependenciesTab = ({ scanId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  
  useEffect(() => {
    loadDependencies();
  }, [scanId]);
  
  const loadDependencies = async () => {
    try {
      const response = await api.get(`/api/litesabre/scans/${scanId}/dependencies`);
      setData(response.data);
    } catch (err) {
      console.error('Failed to load dependencies:', err);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }
  
  if (!data) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>No dependency data available</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4 text-center">
            <Package className="w-8 h-8 mx-auto mb-2 text-blue-500" />
            <div className="text-2xl font-bold">{data.total_packages}</div>
            <div className="text-sm text-gray-500">Total Packages</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-red-500" />
            <div className="text-2xl font-bold">{data.vulnerable_packages}</div>
            <div className="text-sm text-gray-500">Vulnerable</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <AlertOctagon className="w-8 h-8 mx-auto mb-2 text-red-600" />
            <div className="text-2xl font-bold">{data.critical_packages}</div>
            <div className="text-sm text-gray-500">Critical Packages</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <Lock className="w-8 h-8 mx-auto mb-2 text-orange-500" />
            <div className="text-2xl font-bold">{data.high_risk_licenses}</div>
            <div className="text-sm text-gray-500">License Risks</div>
          </CardContent>
        </Card>
      </div>
      
      {/* Dependencies List */}
      <div className="space-y-3">
        {data.dependencies?.map((dep, idx) => (
          <DependencyCard key={idx} dependency={dep} />
        ))}
      </div>
    </div>
  );
};

export default {
  DependencyCard,
  FixPRDialog,
  SecurityOverview,
  DependenciesTab
};
